import {
    Checkbox,
    DefaultButton, IPanelProps,
    IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator, Stack, TextField, Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { fadeIn, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { addUpdateStopLoss } from "global/Reducer/dataSlice";
import { IPanelEvent } from "model/Extension";
import {
    IStopLoss as FAM_IStopLoss
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";

interface IData {
    stopLoss: FAM_IStopLoss | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IData>) => void;

    // start condition
    data?: IData;
}

const StopLossProperties = (props: IProps) => {

    const dispatch = useDispatch();

    const application = useApplication();

    // current view state/stages
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [stopLoss, setStopLoss] = useState<FAM_IStopLoss | null>(null);

    const [cascadeDeactivateFlag, setCascadeDeactivateFlag] = useState<boolean | null>(null);
    const [cascadeDeactivateNonstandard, setCascadeDeactivateNonstandard] = useState<boolean>(false);
    const [cascadeDeactivateAttributeSet, setCascadeDeactivateAttributeSet] = useState<boolean>(false);

    useEffect(() => {
        if (props.isOpen) {

            if (props.data?.stopLoss)
                setStopLoss(structuredClone(props.data.stopLoss));

            setCascadeDeactivateFlag(null);
            setCascadeDeactivateNonstandard(false);
            setCascadeDeactivateAttributeSet(false);

        }
    }, [props.isOpen]);

    function onRenderAddProperties(): JSX.Element {

        if (!stopLoss)
            return <div>Fee and Premium not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>

                    <TextField
                        label='Name'
                        value={stopLoss.name ?? ''}
                        onChange={(event, value) => {
                            setStopLoss(t => { return { ...t!, name: value ?? '' } });
                        }}
                    />

                    <Toggle
                        label={"Deactivate"}
                        checked={stopLoss.deactivated ?? false}
                        disabled={!application.ChangeTypePermissions.stopLoss.modify_deactivate}
                        onChange={(_, checked) => {
                            setStopLoss(t => { return { ...t!, deactivated: checked ?? false } });
                            setCascadeDeactivateFlag(checked ?? false);
                            setCascadeDeactivateNonstandard(checked ?? false);
                            setCascadeDeactivateAttributeSet(checked ?? false);

                        }}
                    />

                    {cascadeDeactivateFlag != null &&
                        <CustomPanel title="Cascade Deactivation Change" >
                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '10px' } }}>
                                <Checkbox
                                    label={`${stopLoss.deactivated ? "Deactivate" : "Activate"} all Nonstandards`}
                                    checked={cascadeDeactivateNonstandard}
                                    disabled={!application.ChangeTypePermissions.stopLoss.modify_deactivate || stopLoss.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateNonstandard(checked ?? false);
                                    }}
                                />

                                <Checkbox
                                    label={`${stopLoss.deactivated ? "Deactivate" : "Activate"} all Attributesets`}
                                    checked={cascadeDeactivateAttributeSet}
                                    disabled={!application.ChangeTypePermissions.stopLoss.modify_deactivate || stopLoss.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateAttributeSet(checked ?? false);
                                    }}
                                />
                            </Stack>

                        </CustomPanel>
                    }

                </Stack>
            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!stopLoss)
            return <div>Plan not available!</div>;

        return <>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
                disabled={stopLoss.name === ''}
                onClick={() => {

                    // TODO for nonstandard
                    // // cascade deactivation to nonstandards and attribute sets
                    // if (cascadeDeactivateNonstandard)
                    //     stopLoss.nonStandards.forEach(eachNonStandard => {
                    //         eachNonStandard.deactivated = stopLoss.deactivated;
                    //     });

                    if (cascadeDeactivateAttributeSet)
                        stopLoss.attributeSets.forEach(eachAttributeSet => {
                            eachAttributeSet.deactivated = stopLoss.deactivated;
                        });

                    dispatch(addUpdateStopLoss([{ stopLoss }]));
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={props.onDismiss}
                styles={panelButtonsStyles}
            />
        </>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown Status</div>)
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        onRender = onRenderAddProperties;

        return (
            <div>
                <Separator />
                {validationMessage &&
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                }

                {onRender()}
            </div>
        );
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;
        onRender = onRenderAddPropertiesFooter;

        return onRender();
    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Properties"
            // onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(StopLossProperties);
